import React, { useState, useEffect } from 'react';
import { IonActionSheet, IonButton, IonButtons, IonCard, IonChip, IonCol, IonContent, IonGrid, IonHeader, IonIcon, IonItem, IonLabel, IonModal, IonRow, IonTitle, IonToast, IonToolbar, IonBadge } from '@ionic/react';
import useNavigation from '../hooks/useNavigation';
import { bugOutline, call, chatbubbleOutline, close, ellipsisVertical, navigate, pulseOutline, locationOutline, calendarOutline, personOutline, businessOutline } from 'ionicons/icons';
import MapViewSelector from './MapViewSelector';
import Map from '../pages/GoogleMap';
import { updatePlotDetails } from '../services/plot.service';
import { getFarmUserAlternateMobile } from '../services/farm.service';
import { makeCall } from '../hooks/common';
import useUserDataStore from '../store/userDataStore';
interface IProps {
    plot: any;
    headerIconOnClickHandler: Function;
}

const PlotCard: React.FC<IProps> = (props) => { 
    const navigateTo = useNavigation();
    const [showActionSheet, setShowActionSheet] = useState(false);
    const [showPlotActions, setShowPlotActions] = useState(false);
    const [showCallSelector, setShowCallSelector] = useState(false);
    const [alternateMobile, setAlternateMobile] = useState<string | null | undefined>(undefined);

    useEffect(() => {
        setAlternateMobile(undefined);
    }, [props.plot.farmUserId]);
    const [showMap, setShowMap] = useState<boolean>(false);
    const [latLng, setLatLng] = useState<[number, number]>();
    const { isPrivilegeUser } = useUserDataStore();
    
    function mapSelector(e: any, lat: number, lng: number) {
        e.stopPropagation();
        setShowActionSheet(true);
    }

    function openMap(lat: number, lng: number) {
        setShowMap(true);
        setLatLng([lat, lng]);
    }

    function closeMap() {
        setShowMap(false);
    }

    async function onPlotsLocationChange(payload: any) {
        await updatePlotDetails(props.plot.plotId, payload);
    }

    async function onPlotsGeoBoundaryChange(payload: any) {
        await updatePlotDetails(props.plot.plotId, payload);
    }

    function addDiseasePestObservation(e: React.MouseEvent<HTMLIonIconElement, MouseEvent>) {
        e.preventDefault();
        e.stopPropagation();
        const plotId = props.plot.plotId || props.plot._id;
        navigateTo('/add-disease-pest-observation', { plotId });
    }


    return (
        <>
            <IonCard onClick={() => navigateTo('/tabs/plot', { plotId: props.plot.plotId || props.plot._id })} style={{ margin: '8px', borderRadius: '12px' }}>
                {/* Header with Plot Name and Actions */}
                <IonItem style={{ '--background': 'transparent', '--border-color': 'transparent' }}>
                    <IonLabel>
                        <h2 style={{ fontWeight: 'bold', fontSize: '16px', margin: '0' }}>
                            {props.plot.name || 'Unnamed Plot'}
                        </h2>
                    </IonLabel>
                    <IonIcon
                        icon={ellipsisVertical}
                        onClick={e => { e.stopPropagation(); setShowPlotActions(true); }}
                        style={{ fontSize: '22px', color: '#3880ff', cursor: 'pointer' }}
                    />
                </IonItem>

                {/* Main Content Grid */}
                <IonGrid style={{ padding: '0 16px 16px 16px' }}>
                    <IonRow>
                        {/* Left Column - Basic Info */}
                        <IonCol size="12" sizeMd="6">
                            {/* Farm & Farmer Info */}
                            <div style={{ marginBottom: '12px' }}>
                                {props.plot.farmId && (
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                                        <IonIcon icon={businessOutline} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                                        <span 
                                            style={{ fontSize: '14px', cursor: 'pointer' }}
                                            onClick={e => { e.stopPropagation(); navigateTo('/tabs/farm', { farmId: props.plot.farmId }) }}
                                        >
                                            {props.plot.farmName || props.plot.farmId}
                                        </span>
                                    </div>
                                )}
                                {props.plot.farmerName && (
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <IonIcon icon={personOutline} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                                        <span style={{ fontSize: '14px' }}>
                                            {props.plot.farmerName}
                                            {props.plot.farmerMobile && (
                                                <IonIcon
                                                    icon={call}
                                                    onClick={async e => {
                                                        e.stopPropagation();
                                                        if (alternateMobile === undefined) {
                                                            const fetched = props.plot.farmUserId
                                                                ? await getFarmUserAlternateMobile(props.plot.farmUserId)
                                                                : null;
                                                            console.log('[call] farmUserId:', props.plot.farmUserId, 'farmerMobile:', props.plot.farmerMobile, 'fetched alternateMobile:', fetched);
                                                            const distinct = fetched && fetched !== props.plot.farmerMobile ? fetched : null;
                                                            setAlternateMobile(distinct);
                                                            if (distinct) {
                                                                setShowCallSelector(true);
                                                            } else {
                                                                makeCall(e as any, props.plot.farmerMobile);
                                                            }
                                                        } else if (alternateMobile) {
                                                            setShowCallSelector(true);
                                                        } else {
                                                            makeCall(e as any, props.plot.farmerMobile);
                                                        }
                                                    }}
                                                    style={{ fontSize: '16px', marginLeft: '8px', color: '#3880ff' }}
                                                />
                                            )}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Location & Distance Info */}
                            <div style={{ marginBottom: '12px' }}>
                                <div 
                                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                                    onClick={e => mapSelector(e, props.plot.location.lat, props.plot.location.lng)}
                                >
                                    <IonIcon icon={locationOutline} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                                    <div style={{ display: 'flex', flexDirection: 'column', fontSize: '14px' }}>
                                        <span>
                                            {props.plot.place}{props.plot.taluka ? ', ' + props.plot.taluka : ''}{props.plot.district ? ', ' + props.plot.district : ''}
                                        </span>
                                        {props.plot?.distanceInMeters && (
                                            <span style={{ color: '#666', fontSize: '12px' }}>
                                                {(props.plot.distanceInMeters / 1000).toFixed(2) + ' Km away'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </IonCol>

                        {/* Right Column - Status & Dates */}
                        <IonCol size="12" sizeMd="6">
                            {/* Device & Status Badges */}
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                                {props.plot?.deviceType && (
                                    <IonBadge color="primary" style={{ fontSize: '12px' }}>
                                        {props.plot.deviceType}
                                    </IonBadge>
                                )}
                                {props.plot?.cropName && (
                                    <IonBadge color="primary" style={{ fontSize: '12px' }}>
                                        {props.plot.cropName}
                                    </IonBadge>
                                )}
                                {props.plot?.plotExpiryDate && !props.plot?.plotIsRecharged && (
                                    <IonBadge 
                                        color={!props.plot?.plotIsRecharged ? "danger" : "warning"}
                                        style={{ fontSize: '12px' }}
                                    >
                                        {!props.plot?.plotIsRecharged ? "Expired" : "Expiring"} on {new Intl.DateTimeFormat('en-GB', {
                                            month: 'short',
                                            day: '2-digit',
                                            year: 'numeric',
                                        }).format(new Date(props.plot.plotExpiryDate))}
                                    </IonBadge>
                                )}
                                {props.plot?.isHiddenByFarmer && (
                                    <IonBadge color="warning" style={{ fontSize: '12px' }}>
                                        Hidden
                                    </IonBadge>
                                )}
                            </div>

                            {/* Service Info & Created Date */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <IonIcon icon={calendarOutline} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                                    <span style={{ fontSize: '12px', color: '#666' }}>
                                        {new Intl.DateTimeFormat('en-GB', {
                                            month: 'short',
                                            day: '2-digit',
                                            year: 'numeric',
                                        }).format(new Date(props.plot.created_date))}
                                    </span>
                                </div>
                                {props.plot?.servicedBy && (
                                    <span style={{ fontSize: '12px', color: '#666' }}>
                                        by {props.plot.servicedBy}
                                    </span>
                                )}
                            </div>
                        </IonCol>
                    </IonRow>
                </IonGrid>
            </IonCard>
            <MapViewSelector isOpen={showActionSheet} withinAppHandler={openMap} onDismiss={setShowActionSheet} lat={props?.plot?.location?.lat} lng={props?.plot?.location?.lng}></MapViewSelector>

            <IonActionSheet
                isOpen={showCallSelector}
                onDidDismiss={() => setShowCallSelector(false)}
                buttons={[
                    {
                        text: props.plot.farmerMobile,
                        icon: call,
                        handler: () => { window.open(`tel:${props.plot.farmerMobile}`); }
                    },
                    {
                        text: alternateMobile!,
                        icon: call,
                        handler: () => { window.open(`tel:${alternateMobile}`); }
                    },
                    {
                        text: 'Cancel',
                        icon: close,
                        role: 'cancel',
                    }
                ]}
            />

            <IonActionSheet
                isOpen={showPlotActions}
                onDidDismiss={() => setShowPlotActions(false)}
                buttons={[
                    {
                        text: 'View Sensor Data',
                        icon: pulseOutline,
                        handler: () => {
                            props.headerIconOnClickHandler({ stopPropagation: () => {} } as any, props.plot);
                        }
                    },
                    ...(isPrivilegeUser ? [{
                        text: 'Add Observation',
                        icon: bugOutline,
                        handler: () => {
                            const plotId = props.plot.plotId || props.plot._id;
                            navigateTo('/add-disease-pest-observation', { plotId });
                        }
                    }] : []),
                    {
                        text: 'Talk to Dharti',
                        icon: chatbubbleOutline,
                        handler: () => {
                            const plotId = props.plot.plotId || props.plot._id;
                            navigateTo('/tabs/dharti', { initialContext: `[Current Plot ID: ${plotId}]\n` });
                        }
                    },
                    {
                        text: 'Cancel',
                        icon: close,
                        role: 'cancel',
                    }
                ]}
            />

            <IonModal isOpen={showMap}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle size='small' style={{ fontWeight: 'bold' }}>
                            Plot Location Viewer
                        </IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={closeMap}>Close</IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                    {
                        !!latLng && <Map
                            plotId={props.plot.plotId}
                            lat={latLng![0]}
                            lng={latLng![1]}
                            onPlotsLocationChange = {onPlotsLocationChange}
                            onPlotsGeoBoundaryChange = {onPlotsGeoBoundaryChange}
                            geoBoundary={props.plot?.geoBoundary?.coordinates[0] ?? []}
                        />
                    }
                </IonContent>
            </IonModal>
        </>
    );
};

export default PlotCard;
